// 表項目編集画面
import React, { useState } from 'react'
import styled from 'styled-components'
import { nanoid } from 'nanoid';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { Button, Box, Paper, IconButton } from '@material-ui/core'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'

import { TableRowType, TableAttribute } from '../redux/cardAttributeDbSlice';
import { TableColumnHashType } from '../redux/cardAttributeModelIndex';
import { deepCopy } from '../general';
import AddTableAttributeRowDialog from '../components/AddTableAttributeRowDialog';
import EditTableAttributeRowDialog from '../components/EditTableAttributeRowDialog';
import DeleteTableAttributeRowDialog from '../components/DeleteTableAttributeRowDialog';

const AddButton = styled(Button)`
  margin-top: 5px;
`
type SortableTableRowProps = {
  dragging: boolean;
  yValue: number;
}

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  container: {
    maxHeight: 440,
  },
})

type TableAreaProps = {
  columnNames: TableColumnHashType;
  ids: string[];
  content: { [key in string]: TableRowType };
  editRowClicked: (id: string) => void;
  deleteRowClicked: (id: string) => void;
  addRowClicked: () => void;
}
const TableArea: React.FC<TableAreaProps> = ({columnNames, ids, content, editRowClicked, deleteRowClicked, addRowClicked}) => {
  const columnIds = Object.keys(columnNames);
  const names = columnIds.map(id => columnNames[id]);  
  const classes = useStyles();

  return (
    <Box>
      <Paper className={classes.root}>
        <TableContainer className={classes.container}>
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow>
                <TableCell style={{width:1}}></TableCell>
                <TableCell style={{width:1}}></TableCell>
                { names.map(columnName => (
                  <TableCell style={{minWidth: '100px'}}>
                    {columnName}
                  </TableCell>
                )) }
              </TableRow>
            </TableHead>
            <TableBody>
              { ids.map(id => {
                const row = content[id];
                const values = columnIds.map(columnId => row.values[columnId]);
                return (
                  <TableRow>
                    <TableCell>
                      <IconButton aria-label="edit" onClick={() => editRowClicked(id)}><EditIcon /></IconButton>
                    </TableCell>
                    <TableCell>
                      <IconButton aria-label="delete" onClick={() => deleteRowClicked(id)}><DeleteIcon /></IconButton>
                    </TableCell>
                    { columnIds.map((columnId, columnIx) =>
                      <TableCell>{values[columnIx]}</TableCell>
                    ) }
                  </TableRow>
                );
              }) }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <AddButton variant='text' color='primary' onClick={() => addRowClicked()}>
        新しい行を追加する
      </AddButton>
    </Box>  
  );
}

type EditTableAttributeDialogProps = {
  open: boolean;
  onClose: () => void;
  item: TableAttribute;
  changeItem: any;
}
const EditTableAttributeDialog: React.FC<EditTableAttributeDialogProps> = ({open, onClose, item, changeItem}) => {
  const [ids, setIds] = useState(Object.keys(item.content));
  const [content, setContent] = useState(item.content);
  type AddRowActionState = { show: boolean };
  const [addRowAction, setAddRowAction] = useState<AddRowActionState>({show: false});
  type EditRowActionState = { show: boolean, id?: string };
  const [editRowAction, setEditRowAction] = useState<EditRowActionState>({show: false});
  type DeleteRowActionState = { show: boolean, id?: string };
  const [deleteRowAction, setDeleteRowAction] = useState<DeleteRowActionState>({show: false});

  const addTableRow = (newRow: TableRowType): void => {
    const newIds = deepCopy(ids);
    const newContent = deepCopy(content);
    const newId = 'ctar:' + nanoid();
    newContent[newId] = newRow;
    newIds.push(newId);
    setIds(newIds);
    setContent(newContent);
  }
  const editTableRow = (id: string, newValue: TableRowType): void => {
    const newContent = deepCopy(content);
    newContent[id] = newValue;
    setContent(newContent);
  }
  const deleteTableRow = (id?: string): void => {
    if (typeof id !== 'string') return;
    const newIds = deepCopy(ids);
    const ix = newIds.indexOf(id);
    if (ix < 0) return;
    newIds.splice(ix, 1);
    setIds(newIds);
    const newContent = deepCopy(content);
    delete newContent[id];
    setContent(newContent);
  }
  const saveTable = () => {
    const newItem = deepCopy(item);
    newItem.content = content;
    changeItem(newItem);
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth='lg'>
      <DialogTitle>項目[{item.name}]の表を編集します</DialogTitle>
      <DialogContent>
        <TableArea columnNames={item.columnNames}
          ids={ids} content={content}
          editRowClicked={(id: string) => setEditRowAction({show: true, id: id})}
          deleteRowClicked={(id: string) => setDeleteRowAction({show: true, id: id})}
          addRowClicked={() => setAddRowAction({show: true})}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='text' color='primary'>
          キャンセル
        </Button>
        <Button variant='text' color='primary' onClick={saveTable}>
          保存する
        </Button>
      </DialogActions>
      {/* 一行追加画面 */}
      <AddTableAttributeRowDialog open={addRowAction.show} 
        onClose={() => setAddRowAction({show: false})}
        item={item} addTableRow={addTableRow}
        rowLength={ids.length} />
      {/* 一行編集画面 */}
      { typeof editRowAction.id === 'string' &&
        <EditTableAttributeRowDialog open={editRowAction.show}
          onClose={() => setEditRowAction({show: false})}
          item={item} id={editRowAction.id}
          row={content[editRowAction.id]}
          editTableRow={editTableRow} />
      }
      {/* 一行削除画面 */}
      <DeleteTableAttributeRowDialog open={deleteRowAction.show}
        onClose={() => setDeleteRowAction({show: false})}
        deleteTableRow={() => deleteTableRow(deleteRowAction.id)} />
    </Dialog>
  );
}

export default EditTableAttributeDialog;
